/**
 * RoutingOverview.jsx
 * 
 * Vista routing per staff nella Journey Operations Dashboard.
 * Design operativo ma moderno e leggibile.
 * 
 * UX Principles:
 * - Overview sintetica in alto
 * - Raggruppamento per fase
 * - Chip/stati ben leggibili
 * - Azioni contestuali vicino all'item
 * - Dettaglio espandibile
 * - Segnali di urgenza sobri ma evidenti
 * 
 * FEATURES:
 * - Invio al fornitore (send-to-supplier)
 * - Registrazione risposta fornitore (staff-mediated)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, Send, Check, X, Clock, AlertTriangle,
  ChevronDown, ChevronUp, MapPin, Calendar, User,
  CheckCircle, XCircle, MessageCircle, Mail, ExternalLink,
  Play, Pause, Flag, Phone, Edit3, HelpCircle, Ticket,
  FileText, Download, UserCheck, Building2
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============== RESPONSE TYPE CONFIG ==============

const RESPONSE_TYPES = {
  confirm: {
    label: 'Confermato',
    description: 'Il fornitore ha confermato',
    color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    icon: CheckCircle
  },
  decline: {
    label: 'Non disponibile',
    description: 'Il fornitore non può confermare',
    color: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100',
    icon: XCircle
  },
  counterproposal: {
    label: 'Proposta alternativa',
    description: 'Il fornitore propone alternative',
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    icon: MessageCircle
  },
  clarification_needed: {
    label: 'Richiesta chiarimenti',
    description: 'Il fornitore chiede info',
    color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    icon: HelpCircle
  }
};

const CHANNEL_SOURCES = {
  email: { label: 'Email', icon: Mail },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle },
  phone: { label: 'Telefono', icon: Phone },
  manual_other: { label: 'Altro', icon: Edit3 }
};

// ============== PHASE DEFINITIONS ==============

const PHASES = {
  needs_review: {
    label: 'Da revisionare',
    icon: Flag,
    color: 'bg-gray-100 text-gray-700',
    description: 'Elementi in attesa di review staff'
  },
  pending_approval: {
    label: 'Attesa cliente',
    icon: User,
    color: 'bg-amber-100 text-amber-700',
    description: 'In attesa di approvazione cliente'
  },
  ready_to_send: {
    label: 'Pronti all\'invio',
    icon: Send,
    color: 'bg-cyan-100 text-cyan-700',
    description: 'Approvati, pronti per invio ai fornitori'
  },
  sent: {
    label: 'In attesa risposta',
    icon: Clock,
    color: 'bg-blue-100 text-blue-700',
    description: 'Inviati ai fornitori, in attesa risposta'
  },
  confirmed: {
    label: 'Confermati',
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    description: 'Confermati dai fornitori'
  },
  needs_attention: {
    label: 'Richiede attenzione',
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-700',
    description: 'Rifiutati, alternative, eccezioni'
  }
};


// ============== OVERVIEW STATS ==============

function RoutingOverviewStats({ routingData }) {
  const summary = routingData?.routing_summary || {};
  const approvalSummary = routingData?.approval_summary || {};
  
  const stats = [
    { 
      label: 'Totale', 
      value: summary.total || 0, 
      color: 'text-gray-700' 
    },
    { 
      label: 'Attesa cliente', 
      value: approvalSummary.pending || 0, 
      color: 'text-amber-600',
      highlight: approvalSummary.pending > 0
    },
    { 
      label: 'Da inviare', 
      value: summary.by_status?.ready_to_send || 0, 
      color: 'text-cyan-600',
      highlight: (summary.by_status?.ready_to_send || 0) > 0
    },
    { 
      label: 'Attesa fornitore', 
      value: summary.pending_supplier_replies || 0, 
      color: 'text-blue-600' 
    },
    { 
      label: 'Confermati', 
      value: summary.by_status?.confirmed || 0, 
      color: 'text-green-600' 
    },
    { 
      label: 'Problemi', 
      value: summary.exception_count || 0, 
      color: 'text-red-600',
      highlight: summary.exception_count > 0,
      alert: true
    }
  ];
  
  return (
    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-4">
      {stats.map((stat, idx) => (
        <div 
          key={idx}
          className={`p-2 rounded-lg text-center ${
            stat.alert && stat.value > 0 
              ? 'bg-red-50 border border-red-200' 
              : stat.highlight 
                ? 'bg-white border border-gray-200 shadow-sm' 
                : 'bg-gray-50'
          }`}
        >
          <p className={`text-lg font-semibold ${stat.color}`}>{stat.value}</p>
          <p className="text-[10px] text-gray-500 truncate">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}


// ============== SEND STATUS DISPLAY ==============

const SEND_STATUS_CONFIG = {
  not_sent: { label: 'Non inviato', color: 'bg-gray-100 text-gray-500', icon: null },
  pending: { label: 'In coda', color: 'bg-yellow-50 text-yellow-600', icon: Clock },
  sending: { label: 'Invio...', color: 'bg-blue-50 text-blue-600', icon: RefreshCw },
  sent: { label: 'Inviato', color: 'bg-green-50 text-green-700', icon: CheckCircle },
  failed: { label: 'Fallito', color: 'bg-red-50 text-red-600', icon: XCircle },
  pending_retry: { label: 'Retry', color: 'bg-amber-50 text-amber-600', icon: RefreshCw }
};


// ============== RESPONSE MODAL ==============

function RecordResponseModal({ isOpen, onClose, item, journeyCode, token, onSuccess }) {
  const [selectedType, setSelectedType] = useState(null);
  const [channelSource, setChannelSource] = useState('email');
  const [providerNote, setProviderNote] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [alternativeDescription, setAlternativeDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  if (!isOpen || !item) return null;
  
  const handleSubmit = async () => {
    if (!selectedType) {
      setError('Seleziona un tipo di risposta');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const body = {
        response_type: selectedType,
        channel_source: channelSource,
        provider_note: providerNote || undefined,
        internal_note: internalNote || undefined
      };
      
      // Se controproposta, aggiungi proposta alternativa
      if (selectedType === 'counterproposal' && alternativeDescription) {
        body.alternative_proposal = {
          description: alternativeDescription,
          requires_customer_approval: true
        };
      }
      
      const res = await fetch(
        `${API_URL}/api/admin/journey/${journeyCode}/routing/${item.item_id}/response`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        }
      );
      
      const result = await res.json();
      
      if (result.success) {
        onSuccess(result);
        onClose();
        // Reset form
        setSelectedType(null);
        setProviderNote('');
        setInternalNote('');
        setAlternativeDescription('');
      } else {
        setError(result.message || 'Errore nella registrazione');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        data-testid="record-response-modal"
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-800">Registra risposta fornitore</h3>
            <button 
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <X size={18} />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {item.title}
          </p>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Error */}
          {error && (
            <div className="p-2 rounded bg-red-50 text-red-700 text-xs flex items-center gap-1">
              <XCircle size={12} />
              {error}
            </div>
          )}
          
          {/* Response Type Selection */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">
              Tipo risposta
            </label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(RESPONSE_TYPES).map(([key, config]) => {
                const Icon = config.icon;
                const isSelected = selectedType === key;
                return (
                  <button
                    key={key}
                    onClick={() => setSelectedType(key)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isSelected 
                        ? config.color + ' border-2' 
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                    data-testid={`response-type-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={16} className={isSelected ? '' : 'text-gray-400'} />
                      <span className="text-sm font-medium">{config.label}</span>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-1">{config.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Channel Source */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-2 block">
              Canale risposta
            </label>
            <div className="flex gap-1">
              {Object.entries(CHANNEL_SOURCES).map(([key, config]) => {
                const Icon = config.icon;
                const isSelected = channelSource === key;
                return (
                  <button
                    key={key}
                    onClick={() => setChannelSource(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 transition-all ${
                      isSelected 
                        ? 'bg-gray-800 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <Icon size={12} />
                    {config.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Alternative Proposal (for counterproposal) */}
          {selectedType === 'counterproposal' && (
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">
                Descrizione alternativa
              </label>
              <textarea
                value={alternativeDescription}
                onChange={(e) => setAlternativeDescription(e.target.value)}
                placeholder="Es: Data alternativa proposta dal fornitore..."
                className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none"
                rows={2}
              />
              <p className="text-[10px] text-purple-600 mt-1">
                Questa alternativa richiederà revisione interna
              </p>
            </div>
          )}
          
          {/* Provider Note */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              Messaggio/nota fornitore (opzionale)
            </label>
            <textarea
              value={providerNote}
              onChange={(e) => setProviderNote(e.target.value)}
              placeholder="Contenuto del messaggio ricevuto..."
              className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none"
              rows={2}
            />
          </div>
          
          {/* Internal Note */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              Nota interna (opzionale)
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Note visibili solo allo staff..."
              className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none"
              rows={2}
            />
          </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={loading || !selectedType}
            className="bg-umbria-olive hover:bg-umbria-olive/90"
            data-testid="submit-response"
          >
            {loading ? (
              <RefreshCw size={14} className="animate-spin mr-1" />
            ) : (
              <Check size={14} className="mr-1" />
            )}
            Registra risposta
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============== ITEM ROW ==============

function RoutingItemRow({ 
  item, 
  onRequestApproval, 
  onSendToSupplier, 
  onRecordResponse,
  onIssueVoucher,
  onDeliverToCustomer,
  onDeliverToProvider,
  onMarkException, 
  loading, 
  sendingItemId,
  issuingVoucherId,
  deliveringCustomerId,
  deliveringProviderId,
  journeyCode,
  token
}) {
  const [expanded, setExpanded] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [responseResult, setResponseResult] = useState(null);
  const [voucherResult, setVoucherResult] = useState(null);
  const [customerDeliveryResult, setCustomerDeliveryResult] = useState(null);
  const [providerDeliveryResult, setProviderDeliveryResult] = useState(null);
  
  const approvalDisplay = item.approval_display || {};
  const routingDisplay = item.routing_display || {};
  
  // Determine send status from item or last execution
  const sendStatus = item.send_status || 'not_sent';
  const sendConfig = SEND_STATUS_CONFIG[sendStatus] || SEND_STATUS_CONFIG.not_sent;
  const SendIcon = sendConfig.icon;
  
  // Is this item currently being sent?
  const isSending = sendingItemId === item.item_id;
  
  // Is this item getting voucher issued?
  const isIssuingVoucher = issuingVoucherId === item.item_id;
  
  // Is this item being delivered?
  const isDeliveringCustomer = deliveringCustomerId === item.item_id;
  const isDeliveringProvider = deliveringProviderId === item.item_id;
  
  // Can show send button?
  const canShowSendButton = item.routing_status === 'ready_to_send' && !item.has_exception;
  
  // Is item awaiting reply? (can record response)
  const isAwaitingReply = ['awaiting_reply', 'sent_to_supplier'].includes(item.routing_status);
  
  // Is item already sent or awaiting reply?
  const isAlreadySent = ['awaiting_reply', 'sent_to_supplier', 'supplier_confirmed'].includes(item.routing_status);
  
  // Is confirmed by supplier?
  const isSupplierConfirmed = item.routing_status === 'supplier_confirmed';
  
  // Has voucher?
  const hasVoucher = item.has_voucher || item.voucher_id;
  
  // Can issue voucher? (confirmed + no voucher yet)
  const canIssueVoucher = isSupplierConfirmed && !hasVoucher && !item.has_exception;
  
  // Delivery status tracking
  const customerDelivered = item.customer_delivery_sent || item.delivery_status === 'delivered_customer' || item.delivery_status === 'delivered_both';
  const providerDelivered = item.provider_delivery_sent || item.delivery_status === 'delivered_provider' || item.delivery_status === 'delivered_both';
  
  // Can deliver? (voucher issued)
  const canDeliverCustomer = hasVoucher && !customerDelivered && !item.has_exception;
  const canDeliverProvider = hasVoucher && isSupplierConfirmed && !providerDelivered && !item.has_exception;
  
  // Is declined or has alternative?
  const isDeclinedOrAlternative = ['supplier_declined', 'counterproposal'].includes(item.routing_status);
  
  // Handle send with feedback
  const handleSend = async () => {
    setSendResult(null);
    const result = await onSendToSupplier(item.item_id);
    if (result) {
      setSendResult(result);
      // Auto-clear success message after 3s
      if (result.success) {
        setTimeout(() => setSendResult(null), 3000);
      }
    }
  };
  
  // Handle response recorded
  const handleResponseSuccess = (result) => {
    setResponseResult(result);
    if (onRecordResponse) {
      onRecordResponse();
    }
    // Auto-clear after 3s
    setTimeout(() => setResponseResult(null), 3000);
  };
  
  // Handle issue voucher
  const handleIssueVoucher = async () => {
    setVoucherResult(null);
    const result = await onIssueVoucher(item.item_id);
    if (result) {
      setVoucherResult(result);
      // Auto-clear success after 3s
      if (result.success) {
        setTimeout(() => setVoucherResult(null), 3000);
      }
    }
  };
  
  // Handle deliver to customer
  const handleDeliverToCustomer = async () => {
    setCustomerDeliveryResult(null);
    const result = await onDeliverToCustomer(item.item_id);
    if (result) {
      setCustomerDeliveryResult(result);
      if (result.success) {
        setTimeout(() => setCustomerDeliveryResult(null), 3000);
      }
    }
  };
  
  // Handle deliver to provider
  const handleDeliverToProvider = async () => {
    setProviderDeliveryResult(null);
    const result = await onDeliverToProvider(item.item_id);
    if (result) {
      setProviderDeliveryResult(result);
      if (result.success) {
        setTimeout(() => setProviderDeliveryResult(null), 3000);
      }
    }
  };
  
  // Get routing status color
  const getStatusBorderColor = () => {
    if (item.has_exception) return 'border-red-200 bg-red-50/30';
    if (customerDelivered && providerDelivered) return 'border-emerald-400 bg-emerald-50/30';
    if (hasVoucher) return 'border-emerald-300 bg-emerald-50/20';
    if (isSupplierConfirmed) return 'border-green-300 bg-green-50/30';
    if (item.routing_status === 'supplier_declined') return 'border-red-200 bg-red-50/20';
    if (item.routing_status === 'counterproposal') return 'border-purple-200 bg-purple-50/20';
    if (isAwaitingReply) return 'border-blue-200 bg-blue-50/20';
    if (item.routing_status === 'ready_to_send') return 'border-cyan-200 bg-cyan-50/20';
    return 'border-gray-200 bg-white';
  };
  
  return (
    <>
    <div 
      data-testid={`routing-item-${item.item_id}`}
      className={`border rounded-lg p-3 transition-all ${getStatusBorderColor()}`}
    >
      <div className="flex items-center gap-3">
        {/* Title & Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800 truncate">
              {item.title}
            </span>
            {item.has_exception && (
              <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
            )}
            {hasVoucher && (
              <Ticket size={14} className="text-emerald-600 flex-shrink-0" />
            )}
            {isSupplierConfirmed && !hasVoucher && (
              <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
            )}
            {item.routing_status === 'supplier_declined' && (
              <XCircle size={14} className="text-red-500 flex-shrink-0" />
            )}
            {item.routing_status === 'counterproposal' && (
              <MessageCircle size={14} className="text-purple-600 flex-shrink-0" />
            )}
            {isAwaitingReply && !isSupplierConfirmed && (
              <Clock size={14} className="text-blue-500 flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
            {item.day && <span>Giorno {item.day}</span>}
            {item.time && <span>{item.time}</span>}
            {item.location && (
              <span className="flex items-center gap-0.5">
                <MapPin size={10} />
                {item.location}
              </span>
            )}
            {item.voucher_code && (
              <span className="flex items-center gap-0.5 text-emerald-600">
                <Ticket size={10} />
                {item.voucher_code}
              </span>
            )}
          </div>
        </div>
        
        {/* Approval Status (hide if supplier already responded) */}
        {!isSupplierConfirmed && !isDeclinedOrAlternative && (
          <Badge className={`${approvalDisplay.color || 'bg-gray-100'} text-[10px]`}>
            {approvalDisplay.label || item.approval_status}
          </Badge>
        )}
        
        {/* Routing Status */}
        <Badge className={`${routingDisplay.color || 'bg-gray-100'} text-[10px]`}>
          {routingDisplay.label_short || item.routing_status}
        </Badge>
        
        {/* Supplier Response Info */}
        {item.supplier_response_at && (
          <span className="text-[10px] text-gray-500">
            {new Date(item.supplier_response_at).toLocaleDateString('it-IT')}
          </span>
        )}
        
        {/* Expand */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-gray-400 hover:text-gray-600"
          data-testid={`expand-${item.item_id}`}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>
      
      {/* Expanded Actions */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {/* Response Result Feedback */}
          {responseResult && (
            <div className="mb-3 p-2 rounded text-xs bg-green-50 text-green-700 border border-green-200">
              <div className="flex items-center gap-1">
                <CheckCircle size={12} />
                {responseResult.message || 'Risposta registrata'}
              </div>
            </div>
          )}
          
          {/* Send Result Feedback */}
          {sendResult && (
            <div className={`mb-3 p-2 rounded text-xs ${
              sendResult.success 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {sendResult.success ? (
                <div className="flex items-center gap-1">
                  <CheckCircle size={12} />
                  {sendResult.simulated ? 'Invio simulato (test)' : 'Email inviata al fornitore'}
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-1 font-medium">
                    <XCircle size={12} />
                    {sendResult.message || 'Invio fallito'}
                  </div>
                  {sendResult.block_reasons && sendResult.block_reasons.length > 0 && (
                    <ul className="mt-1 ml-4 list-disc">
                      {sendResult.block_reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {/* Request approval from customer */}
            {item.approval_status === 'not_requested' && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 gap-1"
                onClick={() => onRequestApproval(item.item_id)}
                disabled={loading}
                data-testid={`request-approval-${item.item_id}`}
              >
                <User size={12} />
                Richiedi approvazione
              </Button>
            )}
            
            {/* Send to supplier - REAL SEND */}
            {canShowSendButton && (
              <Button
                size="sm"
                className="text-xs h-7 gap-1 bg-cyan-600 hover:bg-cyan-700"
                onClick={handleSend}
                disabled={loading || isSending}
                data-testid={`send-to-supplier-${item.item_id}`}
              >
                {isSending ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <Send size={12} />
                )}
                {isSending ? 'Invio...' : 'Invia al fornitore'}
              </Button>
            )}
            
            {/* Record response - for items awaiting reply */}
            {isAwaitingReply && !item.has_exception && (
              <Button
                size="sm"
                className="text-xs h-7 gap-1 bg-blue-600 hover:bg-blue-700"
                onClick={() => setResponseModalOpen(true)}
                disabled={loading}
                data-testid={`record-response-${item.item_id}`}
              >
                <MessageCircle size={12} />
                Registra risposta
              </Button>
            )}
            
            {/* Confirmed by supplier */}
            {isSupplierConfirmed && !hasVoucher && (
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-2 py-1 rounded border border-green-200">
                <CheckCircle size={12} />
                <span>Confermato dal fornitore</span>
                {item.supplier_response_at && (
                  <span className="text-green-600">
                    ({new Date(item.supplier_response_at).toLocaleDateString('it-IT')})
                  </span>
                )}
              </div>
            )}
            
            {/* Issue Voucher - for confirmed items without voucher */}
            {canIssueVoucher && (
              <Button
                size="sm"
                className="text-xs h-7 gap-1 bg-emerald-600 hover:bg-emerald-700"
                onClick={handleIssueVoucher}
                disabled={loading || isIssuingVoucher}
                data-testid={`issue-voucher-${item.item_id}`}
              >
                {isIssuingVoucher ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <Ticket size={12} />
                )}
                {isIssuingVoucher ? 'Emissione...' : 'Emetti voucher'}
              </Button>
            )}
            
            {/* Voucher issued indicator */}
            {hasVoucher && (
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
                <Ticket size={12} />
                <span>Voucher emesso</span>
                {item.voucher_code && (
                  <span className="font-mono text-emerald-600">
                    {item.voucher_code}
                  </span>
                )}
              </div>
            )}
            
            {/* Voucher result feedback */}
            {voucherResult && (
              <div className={`text-xs rounded px-2 py-1 ${
                voucherResult.success 
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {voucherResult.success ? (
                  <div className="flex items-center gap-1">
                    <Ticket size={12} />
                    Voucher emesso: {voucherResult.voucher?.voucher_code}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <XCircle size={12} />
                    {voucherResult.message || 'Errore emissione'}
                  </div>
                )}
              </div>
            )}
            
            {/* ========== DELIVERY SECTION ========== */}
            {/* Invia al cliente - only if voucher issued and not yet delivered */}
            {canDeliverCustomer && (
              <Button
                size="sm"
                className="text-xs h-7 gap-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleDeliverToCustomer}
                disabled={loading || isDeliveringCustomer}
                data-testid={`deliver-customer-${item.item_id}`}
              >
                {isDeliveringCustomer ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <UserCheck size={12} />
                )}
                {isDeliveringCustomer ? 'Invio...' : 'Invia al cliente'}
              </Button>
            )}
            
            {/* Customer delivered indicator */}
            {customerDelivered && (
              <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                <UserCheck size={12} />
                <span>Inviato al cliente</span>
              </div>
            )}
            
            {/* Customer delivery feedback */}
            {customerDeliveryResult && (
              <div className={`text-xs rounded px-2 py-1 ${
                customerDeliveryResult.success 
                  ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {customerDeliveryResult.success ? (
                  <div className="flex items-center gap-1">
                    <Mail size={12} />
                    {customerDeliveryResult.simulated ? 'Email simulata' : 'Email inviata al cliente'}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <XCircle size={12} />
                    {customerDeliveryResult.message || 'Invio fallito'}
                  </div>
                )}
              </div>
            )}
            
            {/* Invia al fornitore - only if voucher issued, confirmed, not yet delivered */}
            {canDeliverProvider && (
              <Button
                size="sm"
                className="text-xs h-7 gap-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleDeliverToProvider}
                disabled={loading || isDeliveringProvider}
                data-testid={`deliver-provider-${item.item_id}`}
              >
                {isDeliveringProvider ? (
                  <RefreshCw size={12} className="animate-spin" />
                ) : (
                  <Building2 size={12} />
                )}
                {isDeliveringProvider ? 'Invio...' : 'Invia al fornitore'}
              </Button>
            )}
            
            {/* Provider delivered indicator */}
            {providerDelivered && (
              <div className="flex items-center gap-1.5 text-xs text-indigo-700 bg-indigo-50 px-2 py-1 rounded border border-indigo-200">
                <Building2 size={12} />
                <span>Inviato al fornitore</span>
              </div>
            )}
            
            {/* Provider delivery feedback */}
            {providerDeliveryResult && (
              <div className={`text-xs rounded px-2 py-1 ${
                providerDeliveryResult.success 
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {providerDeliveryResult.success ? (
                  <div className="flex items-center gap-1">
                    <Mail size={12} />
                    {providerDeliveryResult.simulated ? 'Email simulata' : 'Riepilogo inviato'}
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    <XCircle size={12} />
                    {providerDeliveryResult.message || 'Invio fallito'}
                  </div>
                )}
              </div>
            )}
            
            {/* Declined by supplier */}
            {item.routing_status === 'supplier_declined' && (
              <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                <XCircle size={12} />
                <span>Rifiutato dal fornitore</span>
              </div>
            )}
            
            {/* Counterproposal - needs internal decision */}
            {item.routing_status === 'counterproposal' && (
              <div className="flex items-center gap-2 text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
                <MessageCircle size={12} />
                <span>Proposta alternativa - richiede decisione</span>
              </div>
            )}
            
            {/* Mark exception */}
            {!item.has_exception && !isSupplierConfirmed && !isDeclinedOrAlternative && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 gap-1 text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => onMarkException(item.item_id)}
                disabled={loading}
                data-testid={`mark-exception-${item.item_id}`}
              >
                <AlertTriangle size={12} />
                Eccezione
              </Button>
            )}
            
            {/* Exception info */}
            {item.has_exception && item.exception_type && (
              <div className="flex-1 text-xs text-red-600 flex items-center gap-1">
                <AlertTriangle size={12} />
                Eccezione: {item.exception_type}
              </div>
            )}
          </div>
          
          {/* Provider note (if any) */}
          {item.supplier_response_note && (
            <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
              <span className="font-medium">Nota fornitore:</span>{' '}
              {item.supplier_response_note}
            </div>
          )}
          
          {/* Gate check info when not ready */}
          {item.routing_status !== 'ready_to_send' && !isAlreadySent && !item.has_exception && (
            <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <span className="font-medium">Non pronto all'invio:</span>{' '}
              {item.approval_status !== 'approved' 
                ? 'In attesa approvazione cliente'
                : 'Routing non pronto'}
            </div>
          )}
        </div>
      )}
    </div>
    
    {/* Response Modal */}
    <RecordResponseModal
      isOpen={responseModalOpen}
      onClose={() => setResponseModalOpen(false)}
      item={item}
      journeyCode={journeyCode}
      token={token}
      onSuccess={handleResponseSuccess}
    />
    </>
  );
}


// ============== PHASE GROUP ==============

function PhaseGroup({ 
  phase, 
  items, 
  onRequestApproval, 
  onSendToSupplier, 
  onRecordResponse,
  onIssueVoucher,
  onDeliverToCustomer,
  onDeliverToProvider,
  onMarkException, 
  loading, 
  sendingItemId,
  issuingVoucherId,
  deliveringCustomerId,
  deliveringProviderId,
  journeyCode,
  token 
}) {
  const [collapsed, setCollapsed] = useState(items.length === 0);
  const config = PHASES[phase];
  const Icon = config.icon;
  
  if (items.length === 0) return null;
  
  return (
    <div className="space-y-2">
      {/* Phase Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-2 py-2 hover:bg-gray-50 rounded-lg px-2 transition-colors"
      >
        <div className={`w-6 h-6 rounded-full ${config.color} flex items-center justify-center`}>
          <Icon size={12} />
        </div>
        <span className="text-sm font-medium text-gray-700">{config.label}</span>
        <Badge className="bg-gray-100 text-gray-600 text-[10px]">{items.length}</Badge>
        <div className="flex-1" />
        {collapsed ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronUp size={16} className="text-gray-400" />}
      </button>
      
      {/* Items */}
      {!collapsed && (
        <div className="space-y-2 pl-2">
          {items.map(item => (
            <RoutingItemRow
              key={item.item_id}
              item={item}
              onRequestApproval={onRequestApproval}
              onSendToSupplier={onSendToSupplier}
              onRecordResponse={onRecordResponse}
              onIssueVoucher={onIssueVoucher}
              onDeliverToCustomer={onDeliverToCustomer}
              onDeliverToProvider={onDeliverToProvider}
              onMarkException={onMarkException}
              loading={loading}
              sendingItemId={sendingItemId}
              issuingVoucherId={issuingVoucherId}
              deliveringCustomerId={deliveringCustomerId}
              deliveringProviderId={deliveringProviderId}
              journeyCode={journeyCode}
              token={token}
            />
          ))}
        </div>
      )}
    </div>
  );
}


// ============== MAIN COMPONENT ==============

export default function RoutingOverview({ journeyCode, token }) {
  const [routingData, setRoutingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [sendingItemId, setSendingItemId] = useState(null);
  const [issuingVoucherId, setIssuingVoucherId] = useState(null);
  const [deliveringCustomerId, setDeliveringCustomerId] = useState(null);
  const [deliveringProviderId, setDeliveringProviderId] = useState(null);
  const [error, setError] = useState(null);
  
  const fetchRouting = useCallback(async () => {
    if (!token || !journeyCode) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/routing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setRoutingData(data);
      } else {
        setError('Errore nel caricamento');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [journeyCode, token]);
  
  useEffect(() => {
    fetchRouting();
  }, [fetchRouting]);
  
  // Refresh when response is recorded
  const handleResponseRecorded = () => {
    fetchRouting();
  };
  
  const handleRequestApproval = async (itemId) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/approvals/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ item_id: itemId })
      });
      
      if (res.ok) {
        await fetchRouting();
      }
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleSendToSupplier = async (itemId) => {
    setSendingItemId(itemId);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/routing/${itemId}/send-to-supplier`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await res.json();
      
      if (result.success) {
        // Refresh routing data after successful send
        await fetchRouting();
      }
      
      return result;
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setSendingItemId(null);
    }
  };
  
  const handleDeliverToCustomer = async (itemId) => {
    setDeliveringCustomerId(itemId);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/routing/${itemId}/voucher/deliver/customer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await res.json();
      
      if (result.success) {
        await fetchRouting();
      }
      
      return result;
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setDeliveringCustomerId(null);
    }
  };
  
  const handleDeliverToProvider = async (itemId) => {
    setDeliveringProviderId(itemId);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/routing/${itemId}/voucher/deliver/provider`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await res.json();
      
      if (result.success) {
        await fetchRouting();
      }
      
      return result;
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setDeliveringProviderId(null);
    }
  };
  
  const handleMarkException = async (itemId) => {
    const note = prompt('Descrivi il problema:');
    if (!note) return;
    
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/routing/${itemId}/exception`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ exception_type: 'manual', note })
      });
      
      if (res.ok) {
        await fetchRouting();
      }
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleIssueVoucher = async (itemId) => {
    setIssuingVoucherId(itemId);
    try {
      const res = await fetch(`${API_URL}/api/admin/journey/${journeyCode}/routing/${itemId}/voucher`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await res.json();
      
      if (result.success) {
        // Refresh routing data after successful voucher issue
        await fetchRouting();
      }
      
      return result;
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setIssuingVoucherId(null);
    }
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="h-5 w-5 animate-spin text-umbria-olive" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-red-600">
          {error}
        </CardContent>
      </Card>
    );
  }
  
  const phases = routingData?.phases || {};
  
  return (
    <Card data-testid="routing-overview">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Send size={16} className="text-umbria-olive" />
            Routing & Invio Fornitori
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchRouting}
            disabled={loading}
            className="h-7 px-2"
            data-testid="refresh-routing"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Overview Stats */}
        <RoutingOverviewStats routingData={routingData} />
        
        {/* Phase Groups */}
        <div className="space-y-4">
          <PhaseGroup
            phase="needs_attention"
            items={phases.needs_attention || []}
            onRequestApproval={handleRequestApproval}
            onSendToSupplier={handleSendToSupplier}
            onRecordResponse={handleResponseRecorded}
            onIssueVoucher={handleIssueVoucher}
            onDeliverToCustomer={handleDeliverToCustomer}
            onDeliverToProvider={handleDeliverToProvider}
            onMarkException={handleMarkException}
            loading={actionLoading}
            sendingItemId={sendingItemId}
            issuingVoucherId={issuingVoucherId}
            deliveringCustomerId={deliveringCustomerId}
            deliveringProviderId={deliveringProviderId}
            journeyCode={journeyCode}
            token={token}
          />
          <PhaseGroup
            phase="pending_approval"
            items={phases.pending_approval || []}
            onRequestApproval={handleRequestApproval}
            onSendToSupplier={handleSendToSupplier}
            onRecordResponse={handleResponseRecorded}
            onIssueVoucher={handleIssueVoucher}
            onDeliverToCustomer={handleDeliverToCustomer}
            onDeliverToProvider={handleDeliverToProvider}
            onMarkException={handleMarkException}
            loading={actionLoading}
            sendingItemId={sendingItemId}
            issuingVoucherId={issuingVoucherId}
            deliveringCustomerId={deliveringCustomerId}
            deliveringProviderId={deliveringProviderId}
            journeyCode={journeyCode}
            token={token}
          />
          <PhaseGroup
            phase="ready_to_send"
            items={phases.ready_to_send || []}
            onRequestApproval={handleRequestApproval}
            onSendToSupplier={handleSendToSupplier}
            onRecordResponse={handleResponseRecorded}
            onIssueVoucher={handleIssueVoucher}
            onDeliverToCustomer={handleDeliverToCustomer}
            onDeliverToProvider={handleDeliverToProvider}
            onMarkException={handleMarkException}
            loading={actionLoading}
            sendingItemId={sendingItemId}
            issuingVoucherId={issuingVoucherId}
            deliveringCustomerId={deliveringCustomerId}
            deliveringProviderId={deliveringProviderId}
            journeyCode={journeyCode}
            token={token}
          />
          <PhaseGroup
            phase="sent"
            items={phases.sent || []}
            onRequestApproval={handleRequestApproval}
            onSendToSupplier={handleSendToSupplier}
            onRecordResponse={handleResponseRecorded}
            onIssueVoucher={handleIssueVoucher}
            onDeliverToCustomer={handleDeliverToCustomer}
            onDeliverToProvider={handleDeliverToProvider}
            onMarkException={handleMarkException}
            loading={actionLoading}
            sendingItemId={sendingItemId}
            issuingVoucherId={issuingVoucherId}
            deliveringCustomerId={deliveringCustomerId}
            deliveringProviderId={deliveringProviderId}
            journeyCode={journeyCode}
            token={token}
          />
          <PhaseGroup
            phase="confirmed"
            items={phases.confirmed || []}
            onRequestApproval={handleRequestApproval}
            onSendToSupplier={handleSendToSupplier}
            onRecordResponse={handleResponseRecorded}
            onIssueVoucher={handleIssueVoucher}
            onDeliverToCustomer={handleDeliverToCustomer}
            onDeliverToProvider={handleDeliverToProvider}
            onMarkException={handleMarkException}
            loading={actionLoading}
            sendingItemId={sendingItemId}
            issuingVoucherId={issuingVoucherId}
            deliveringCustomerId={deliveringCustomerId}
            deliveringProviderId={deliveringProviderId}
            journeyCode={journeyCode}
            token={token}
          />
          <PhaseGroup
            phase="needs_review"
            items={phases.needs_review || []}
            onRequestApproval={handleRequestApproval}
            onSendToSupplier={handleSendToSupplier}
            onRecordResponse={handleResponseRecorded}
            onIssueVoucher={handleIssueVoucher}
            onDeliverToCustomer={handleDeliverToCustomer}
            onDeliverToProvider={handleDeliverToProvider}
            onMarkException={handleMarkException}
            loading={actionLoading}
            sendingItemId={sendingItemId}
            issuingVoucherId={issuingVoucherId}
            deliveringCustomerId={deliveringCustomerId}
            deliveringProviderId={deliveringProviderId}
            journeyCode={journeyCode}
            token={token}
          />
        </div>
        
        {/* Empty state */}
        {Object.values(phases).every(p => p.length === 0) && (
          <div className="text-center py-8 text-gray-500 text-sm">
            Nessun item nel routing
          </div>
        )}
      </CardContent>
    </Card>
  );
}
